import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import agent from '../api/axios';
import { formatDateDisplay } from '../utils/dateUtils';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Local interface matching server DTO
interface SubscriptionDto {
  id?: string;
  name?: string;
  term?: string;
  active?: boolean;
  price?: number;
  stripeProductId?: string;
  stripePriceId?: string;
  createdDate?: string;
  updatedDate?: string;
}

export default function Subscriptions() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [items, setItems] = useState<SubscriptionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<SubscriptionDto | null>(null);
  const [form, setForm] = useState<SubscriptionDto>({
    name: '',
    active: true,
  });
  const [errors, setErrors] = useState<{
    name?: string;
    term?: string;
    price?: string;
  }>({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState<string | null>(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const data = await agent.Subscriptions.get();
      const mapped = (data ?? []).map((s: any) => {
        const id = s.id ?? s._id ?? s.Id;
        const name = s.name ?? s.Name;
        const term = s.term ?? s.Term;
        const price = s.price ?? s.Price ?? undefined;
        const active = (s.active ?? s.Active) === true;
        const createdDate = s.createdDate ?? s.CreatedDate;
        return {
          id,
          name,
          term,
          price,
          active,
          createdDate,
          stripePriceId: s.stripePriceId ?? s.StripePriceId,
          updatedDate: s.updatedDate ?? s.UpdatedDate,
        } as SubscriptionDto;
      });
      setItems(mapped);
    } catch (err) {
      console.error('Failed to load subscriptions', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({ name: '', active: true });
    setErrors({});
  };

  const openCreate = () => {
    if (items.length >= 2) {
      enqueueSnackbar(
        'Maximum of 2 subscription plans allowed (Monthly and Yearly)',
        { variant: 'warning' }
      );
      return;
    }
    setEditing(null);
    resetForm();
    setOpenDialog(true);
  };

  const openEdit = (b: SubscriptionDto) => {
    setEditing(b);
    setForm({
      ...b,
      term: b.term,
      price: b.price,
      active: b.active ?? true,
      name: b.name ?? '',
    });
    setErrors({});
    setOpenDialog(true);
  };

  const validateForm = (): boolean => {
    const e: typeof errors = {};
    if (!form.name || String(form.name).trim() === '') {
      e.name = 'Name is required';
    }
    if (!form.term || String(form.term).trim() === '') {
      e.term = 'Term is required';
    } else {
      const normalizedTerm = String(form.term).toLowerCase();
      if (normalizedTerm !== 'monthly' && normalizedTerm !== 'yearly') {
        e.term = 'Term must be either "Monthly" or "Yearly"';
      }
    }
    const price = Number.isFinite(Number(form.price))
      ? Number(form.price)
      : NaN;
    if (!Number.isFinite(price) || price < 0) {
      e.price = 'Price must be 0 or greater';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    try {
      if (!validateForm()) return;

      if (!editing) {
        const normalizedTerm = String(form.term).toLowerCase();
        const termExists = items.some(
          (item) => String(item.term || '').toLowerCase() === normalizedTerm
        );

        if (termExists) {
          enqueueSnackbar(`A ${form.term} subscription plan already exists`, {
            variant: 'error',
          });
          return;
        }
      }

      const payload: any = {
        name: String(form.name).trim(),
        term: String(form.term).trim(),
        active: !!form.active,
        price: Number(form.price ?? 0),
      };

      if (editing && editing.id) {
        await agent.Subscriptions.update(editing.id, payload);
        enqueueSnackbar('Subscription updated successfully!', {
          variant: 'success',
        });
      } else {
        await agent.Subscriptions.create(payload);
        enqueueSnackbar('Subscription created successfully!', {
          variant: 'success',
        });
      }
      setOpenDialog(false);
      fetch();
    } catch (err: any) {
      console.error('Save failed', err);
      enqueueSnackbar(
        err?.response?.data?.Error ||
          err?.response?.data?.message ||
          'Failed to save subscription. Please try again.',
        { variant: 'error' }
      );
    }
  };

  const openConfirmDelete = (id?: string, name?: string) => {
    if (!id) return;
    setConfirmId(id);
    setConfirmName(name ?? null);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmId) return;
    try {
      await agent.Subscriptions.delete(confirmId);
      enqueueSnackbar('Subscription deleted successfully!', {
        variant: 'success',
      });
      setConfirmOpen(false);
      setConfirmId(null);
      setConfirmName(null);
      fetch();
    } catch (err: any) {
      console.error('Delete failed', err);
      enqueueSnackbar(
        err?.response?.data?.message ||
          'Failed to delete subscription. Please try again.',
        { variant: 'error' }
      );
      setConfirmOpen(false);
    }
  };

  const isFormValid = (): boolean => {
    return (
      !!form.name &&
      String(form.name).trim() !== '' &&
      !!form.term &&
      String(form.term).trim() !== '' &&
      Number.isFinite(Number(form.price)) &&
      Number(form.price) >= 0
    );
  };

  const numberFieldSx = {
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  } as const;

  // Mobile Card View
  const renderMobileCards = () => (
    <Grid container spacing={2}>
      {items.map((s) => (
        <Grid item xs={12} key={s.id ?? JSON.stringify(s)}>
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AttachMoneyIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {s.name}
                    </Typography>
                    <Chip
                      label={s.term}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Chip
                  label={s.active ? 'Active' : 'Inactive'}
                  size="small"
                  color={s.active ? 'success' : 'default'}
                />
              </Box>

              <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Price
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {s.price != null ? `$${Number(s.price).toFixed(2)}` : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {s.createdDate ? formatDateDisplay(s.createdDate) : '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => openEdit(s)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => openConfirmDelete(s.id, s.name)}
                >
                  Delete
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Desktop Table View
  const renderDesktopTable = () => (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Term</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Created</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((s) => (
            <TableRow key={s.id ?? JSON.stringify(s)}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.term ?? ''}</TableCell>
              <TableCell>
                {s.price != null ? `$${Number(s.price).toFixed(2)}` : ''}
              </TableCell>
              <TableCell>{s.active ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                {s.createdDate ? formatDateDisplay(s.createdDate) : ''}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label={`edit ${s.name}`}
                  onClick={() => openEdit(s)}
                >
                  <EditIcon sx={{ color: 'warning.main' }} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`delete ${s.name}`}
                  onClick={() => openConfirmDelete(s.id, s.name)}
                >
                  <DeleteIcon sx={{ color: 'error.main' }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ maxWidth: 1100, m: 'auto', p: { xs: 2, sm: 3 } }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'success.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
          }}
        >
          <SubscriptionsIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Subscriptions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage subscription plans
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          onClick={openCreate}
          disabled={items.length >= 2}
        >
          Create Plan
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SubscriptionsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No subscription plans
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create Monthly and Yearly plans
          </Typography>
        </Paper>
      ) : isMobile ? (
        renderMobileCards()
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Plans ({items.length}/2)
          </Typography>
          {renderDesktopTable()}
        </Paper>
      )}

      <Modal
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setErrors({});
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
            width: '90%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="h6" mb={3}>
            {editing ? 'Edit Subscription' : 'Create Subscription'}
          </Typography>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mt={1}>
            <TextField
              required
              label="Name"
              value={form.name ?? ''}
              onChange={(e) => {
                setForm((s) => ({ ...s, name: e.target.value }));
                setErrors((prev) => ({ ...prev, name: undefined }));
              }}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />
            <TextField
              required
              label="Term"
              value={form.term ?? ''}
              onChange={(e) => {
                setForm((s) => ({ ...s, term: e.target.value }));
                setErrors((prev) => ({ ...prev, term: undefined }));
              }}
              helperText={
                errors.term ?? 'Must be "Monthly" or "Yearly"'
              }
              error={!!errors.term}
              fullWidth
            />
            <TextField
              required
              label="Price"
              type="number"
              sx={numberFieldSx}
              inputProps={{ min: 0, step: '0.01' }}
              value={form.price ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setForm((s) => ({
                  ...s,
                  price: val === '' ? undefined : Number(val),
                }));
                setErrors((prev) => ({ ...prev, price: undefined }));
              }}
              error={!!errors.price}
              helperText={errors.price}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.active}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, active: e.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Box>

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}
          >
            <Button
              onClick={() => {
                setOpenDialog(false);
                setErrors({});
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              {editing ? 'Save' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
            width: '90%',
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" mb={2}>
            Confirm Delete
          </Typography>
          <Typography variant="body1" mb={3}>
            Are you sure you want to delete{' '}
            {confirmName ? `'${confirmName}'` : 'this subscription'}?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              color="error"
              onClick={handleConfirmDelete}
              variant="contained"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
